import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Allineato a App.tsx interactionTl (hero pin +=120%, manifesto y 100vh → 0).
var HERO_MANIFESTO_PIN = {
  panelIn: 0.1,
  panelDuration: 1,
  wordsIn: 0.6,
  wordsDuration: 1,
  wordsStagger: 0.02,
  deepDiveIn: 1,
  deepDiveDuration: 0.5,
};

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getManifestoTargets(hero) {
  return {
    words: hero.querySelectorAll('.camst-manifesto__word-inner'),
    richHeadline: hero.querySelector('[data-camst-headline-rich]'),
    deepDive: hero.querySelector('.camst-manifesto__deep-dive'),
    panel: hero.querySelector('.camst-manifesto__panel'),
  };
}

function setManifestoVisible(hero, manifestoRoot) {
  var targets = getManifestoTargets(hero);

  gsap.set(targets.words, { yPercent: 0 });

  if (targets.richHeadline) {
    gsap.set(targets.richHeadline, { opacity: 1, y: 0 });
  }

  if (targets.panel) {
    gsap.set(targets.panel, { y: 0, clearProps: 'transform' });
  }

  if (targets.deepDive) {
    gsap.set(targets.deepDive, { opacity: 1, y: 0 });
  }

  if (manifestoRoot) {
    manifestoRoot.classList.add('is-complete');
  }

  hero.classList.add('camst-hero--manifesto-complete');
}

function animateManifestoHeadline(timeline, targets, position, duration, stagger) {
  if (targets.words.length) {
    timeline.to(
      targets.words,
      {
        yPercent: 0,
        stagger: stagger,
        duration: duration,
        ease: 'power2.out',
      },
      position
    );
    return;
  }

  if (targets.richHeadline) {
    timeline.fromTo(
      targets.richHeadline,
      { opacity: 0, y: 24 },
      {
        opacity: 1,
        y: 0,
        duration: duration * 0.7,
        ease: 'power2.out',
      },
      position
    );
  }
}

function syncManifestoTimeline(manifestoRoot, timeline) {
  if (!timeline || !timeline.scrollTrigger) {
    return;
  }

  ScrollTrigger.refresh();
  timeline.progress(timeline.scrollTrigger.progress);

  if (timeline.scrollTrigger.progress >= 0.85 && manifestoRoot) {
    manifestoRoot.classList.add('is-complete');
  }
}

function findLinkedHero(manifestoRoot) {
  var widget = manifestoRoot.closest('.elementor-widget-camst-manifesto');

  if (!widget) {
    return document.querySelector('[data-camst-hero]');
  }

  var element = widget.closest('.elementor-element');

  while (element) {
    var previous = element.previousElementSibling;

    if (!previous) {
      var parent = element.parentElement;

      if (!parent || parent === document.body) {
        break;
      }

      element = parent.closest('.elementor-element') || parent;
      continue;
    }

    element = previous;
    var hero = element.querySelector('[data-camst-hero]');

    if (hero) {
      return hero;
    }
  }

  return document.querySelector('[data-camst-hero]');
}

function collapseManifestoHost(manifestoRoot) {
  manifestoRoot.classList.add('is-hero-integrated');
  manifestoRoot.style.margin = '0';
  manifestoRoot.style.padding = '0';
  manifestoRoot.style.height = '0';
  manifestoRoot.style.minHeight = '0';
  manifestoRoot.style.overflow = 'hidden';

  var track = manifestoRoot.querySelector('.camst-manifesto__scroll-track');

  if (track) {
    track.style.height = '0';
    track.style.minHeight = '0';
  }

  var hostWidget = manifestoRoot.closest('.elementor-widget-camst-manifesto');

  if (hostWidget) {
    hostWidget.style.margin = '0';
    hostWidget.style.padding = '0';
  }
}

function mountManifestoInHero(hero, manifestoRoot) {
  var panel = manifestoRoot.querySelector('.camst-manifesto__panel');

  if (!panel || hero.querySelector('.camst-hero__manifesto-mount .camst-manifesto__panel')) {
    return panel;
  }

  var mount = hero.querySelector('.camst-hero__manifesto-mount');

  if (!mount) {
    mount = document.createElement('div');
    mount.className = 'camst-hero__manifesto-mount';
    mount.setAttribute('aria-hidden', 'true');
    hero.appendChild(mount);
  }

  panel.classList.add('camst-manifesto__panel--hero-overlay');
  mount.appendChild(panel);
  hero.classList.add('camst-hero--has-manifesto');
  manifestoRoot.dataset.camstManifestoHeroIntegrated = 'true';

  return panel;
}

export function tryIntegrateManifestoWithHero(manifestoRoot) {
  if (
    !manifestoRoot ||
    manifestoRoot.dataset.camstManifestoHeroIntegrated === 'true' ||
    manifestoRoot.dataset.animation !== 'pin_overlay'
  ) {
    return false;
  }

  var hero = findLinkedHero(manifestoRoot);

  if (!hero) {
    return false;
  }

  var panel = mountManifestoInHero(hero, manifestoRoot);

  if (!panel) {
    return false;
  }

  collapseManifestoHost(manifestoRoot);

  if (prefersReducedMotion()) {
    setManifestoVisible(hero, manifestoRoot);
    manifestoRoot.dataset.camstManifestoReady = 'true';
    return true;
  }

  var targets = getManifestoTargets(hero);
  targets.panel = panel;

  manifestoRoot.classList.add('is-animating');
  manifestoRoot.dataset.camstManifestoReady = 'true';

  gsap.set(targets.words, { yPercent: 101 });

  if (targets.richHeadline) {
    gsap.set(targets.richHeadline, { opacity: 0, y: 24 });
  }

  if (targets.deepDive) {
    gsap.set(targets.deepDive, { opacity: 0, y: 40 });
  }

  gsap.set(panel, { y: '100vh' });

  var pinLength = parseFloat(manifestoRoot.dataset.pinLength || '120') / 100;
  var scrub = parseFloat(manifestoRoot.dataset.scrub || '1.5');

  var pinTl = gsap.timeline({
    scrollTrigger: {
      trigger: hero,
      start: 'top top',
      end: '+=' + Math.round(pinLength * 100) + '%',
      pin: true,
      scrub: scrub,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      id: 'camst-hero-manifesto-pin',
      onUpdate: function (self) {
        if (self.progress >= 0.85) {
          gsap.set(panel, { y: 0, clearProps: 'transform' });
          manifestoRoot.classList.add('is-complete');
          hero.classList.add('camst-hero--manifesto-complete');
        }
      },
      onLeave: function () {
        gsap.set(panel, { y: 0, clearProps: 'transform' });
        manifestoRoot.classList.add('is-complete');
        hero.classList.add('camst-hero--manifesto-complete');
      },
    },
  });

  pinTl.fromTo(
    panel,
    { y: '100vh' },
    {
      y: 0,
      duration: HERO_MANIFESTO_PIN.panelDuration,
      ease: 'none',
    },
    HERO_MANIFESTO_PIN.panelIn
  );

  animateManifestoHeadline(
    pinTl,
    targets,
    HERO_MANIFESTO_PIN.wordsIn,
    HERO_MANIFESTO_PIN.wordsDuration,
    HERO_MANIFESTO_PIN.wordsStagger
  );

  if (targets.deepDive) {
    pinTl.fromTo(
      targets.deepDive,
      { opacity: 0, y: 40 },
      {
        opacity: 1,
        y: 0,
        duration: HERO_MANIFESTO_PIN.deepDiveDuration,
        ease: 'power2.out',
      },
      HERO_MANIFESTO_PIN.deepDiveIn
    );
  }

  syncManifestoTimeline(manifestoRoot, pinTl);

  window.requestAnimationFrame(function () {
    syncManifestoTimeline(manifestoRoot, pinTl);
    ScrollTrigger.refresh();
  });

  window.addEventListener(
    'load',
    function () {
      syncManifestoTimeline(manifestoRoot, pinTl);
      ScrollTrigger.refresh();
    },
    { once: true }
  );

  return true;
}

export function teardownHeroManifestoIntegration(manifestoRoot) {
  if (!manifestoRoot || manifestoRoot.dataset.camstManifestoHeroIntegrated !== 'true') {
    return;
  }

  ScrollTrigger.getAll().forEach(function (st) {
    if (st.vars && st.vars.id === 'camst-hero-manifesto-pin') {
      st.kill();
    }
  });

  delete manifestoRoot.dataset.camstManifestoHeroIntegrated;
  manifestoRoot.classList.remove('is-hero-integrated');
  manifestoRoot.style.cssText = '';

  var track = manifestoRoot.querySelector('.camst-manifesto__scroll-track');

  if (track) {
    track.style.cssText = '';
  }

  var hero = document.querySelector('.camst-hero--has-manifesto');

  if (hero) {
    hero.classList.remove('camst-hero--has-manifesto', 'camst-hero--manifesto-complete');
  }
}
